// frontend/src/pages/CalendarioPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import EventoService from '../../services/eventoService';
import CrearEventoModal from '../../components/CrearEventoModal';
import ModificarEventoModal from '../../components/ModificarEventoModal';
import CustomModal from '../../components/CustomModal';
import useCustomModal from '../../hooks/useCustomModal';
import { useCalendario } from '../../contexts/CalendarioContext';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import '../../style/pages/CalendarioPage.css';

const CalendarioPage = () => {
    const navigate = useNavigate();
    const { feriados } = useCalendario();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [appointments, setAppointments] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModificarModalOpen, setIsModificarModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [eventoToEdit, setEventoToEdit] = useState(null);
    const [isLoadingEvents, setIsLoadingEvents] = useState(false);
    const [view, setView] = useState('month'); // 'month', 'week', 'day'

    const { modalState, hideModal, showSuccess, showError, showConfirm } = useCustomModal();

    const fetchAppointments = async (silencioso = false) => {
        try {
            if (!silencioso) setIsLoadingEvents(true);
            const response = await EventoService.getEventos({ _: Date.now() });
            const formattedAppointments = response.data.map(app => ({
                id: app.idEvento,
                date: app.fecha,
                time: app.horaInicio,
                client: app.titulo,
                type: app.tipoEvento,
                description: app.descripcion,
                duration: app.horaFin ? `${app.horaInicio} - ${app.horaFin}` : '',
                todoElDia: app.todoElDia,
                idTramite: app.idTramite,
                horaInicio: app.horaInicio,
                horaFin: app.horaFin,
                visibilidad: app.visibilidad || 'PUBLICO',
                nombreCompletoCliente: app.nombreCompletoCliente || null,
                cuilCliente: app.cuilCliente || null
            }));
            setAppointments(formattedAppointments);
        } catch (error) {
            if (!silencioso) console.error("Error al cargar eventos:", error);
            if (!silencioso) showError("Error al cargar los eventos");
        } finally {
            if (!silencioso) setIsLoadingEvents(false);
        }
    };

    // Refs para mantener estado entre renders
    const timeoutRef = useRef(null);
    const lastUpdateRef = useRef(0);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        fetchAppointments();

        // Escuchar evento personalizado cuando se crea un evento desde el chat
        const handleEventoCreado = (event) => {
            // Verificar que el componente esté montado
            if (!isMountedRef.current) {
                return;
            }

            console.log('📅 CalendarioPage: Evento detectado - actualizando eventos después de creación desde chat...', event.detail);

            // Evitar múltiples actualizaciones muy seguidas (debounce)
            const now = Date.now();
            if (now - lastUpdateRef.current < 2000) {
                console.log('📅 CalendarioPage: Ignorando actualización (muy reciente)');
                return;
            }

            // Limpiar timeout anterior si existe
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            // Esperar un poco para asegurar que el backend procesó el evento
            timeoutRef.current = setTimeout(() => {
                // Verificar nuevamente que el componente esté montado antes de actualizar
                if (isMountedRef.current) {
                    console.log('📅 CalendarioPage: Actualizando eventos desde chat...');
                    lastUpdateRef.current = Date.now();
                    fetchAppointments();
                }
                timeoutRef.current = null;
            }, 800);
        };

        window.addEventListener('eventoCreadoDesdeChat', handleEventoCreado);

        // Refresco silencioso cada 60 segundos
        const intervaloId = setInterval(() => {
            if (isMountedRef.current) fetchAppointments(true);
        }, 60000);

        // Cleanup
        return () => {
            isMountedRef.current = false;
            window.removeEventListener('eventoCreadoDesdeChat', handleEventoCreado);
            clearInterval(intervaloId);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps

    const handleSaveEvento = async (eventoData) => {
        try {
            await EventoService.crearEvento(eventoData);
            setIsModalOpen(false);
            await new Promise(r => setTimeout(r, 150));
            await fetchAppointments();

            // Consultar notificaciones inmediatamente después de crear el evento
            if (window.consultarNotificacionesInmediatas) {
                window.consultarNotificacionesInmediatas();
            }

            // Delay de 2-3 segundos antes de mostrar el modal de confirmación
            setTimeout(() => {
                showSuccess("Evento creado correctamente");
            }, 2500);
        } catch (error) {
            console.error("Error al crear el evento:", error);
            showError("Error al crear el evento");
        }
    };

    const handleModificarEvento = async (idEvento, eventoData) => {
        try {
            await EventoService.modificarEvento(idEvento, eventoData);
            setIsModificarModalOpen(false);
            setEventoToEdit(null);
            showSuccess("Evento modificado correctamente");
            await new Promise(r => setTimeout(r, 150));
            await fetchAppointments();

            if (window.recargarCampanaNotificaciones) {
                window.recargarCampanaNotificaciones();
            }
        } catch (error) {
            console.error("Error al modificar el evento:", error);
            showError("Error al modificar el evento");
        }
    };

    const handleEliminarEvento = (idEvento) => {
        showConfirm(
            '¿Estás seguro de que quieres eliminar este evento?',
            async () => {
                try {
                    await EventoService.eliminarEvento(idEvento);
                    showSuccess('Evento eliminado correctamente');
                    await fetchAppointments();
                    setSelectedEvent(null);
                } catch (error) {
                    console.error("Error al eliminar el evento:", error);
                    showError('Error al eliminar el evento');
                }
            },
            "Confirmar eliminación"
        );
    };

    const getAppointmentsForDate = (date) => {
        const dateString = date.toISOString().split('T')[0]; // formato yyyy-MM-dd
        return appointments.filter(app => {
            // Normalizar app.date a formato yyyy-MM-dd si viene en otro formato
            let appDateNormalized = app.date;
            
            // Si viene en formato dd-MM-yyyy o dd/MM/yyyy, convertir a yyyy-MM-dd
            if (app.date && app.date.match(/^\d{2}[-/]\d{2}[-/]\d{4}$/)) {
                const parts = app.date.split(/[-/]/);
                appDateNormalized = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
            }
            
            return appDateNormalized === dateString;
        });
    };

    const esFeriado = (date) => {
        const dateString = date.toISOString().split('T')[0];
        return feriados.some(feriado => feriado.fecha === dateString);
    };

    const getFeriadoInfo = (date) => {
        const dateString = date.toISOString().split('T')[0];
        return feriados.find(feriado => feriado.fecha === dateString);
    };

    const getFeriadoColor = (tipo) => {
        const colores = {
            'NACIONAL': '#ef4444',      // Rojo (inamovible)
            'TRASLADABLE': '#3b82f6',   // Azul
            'PUENTE': '#10b981',        // Verde
            'NO_LABORABLE': '#f59e0b',  // Amarillo/Naranja
            'PROVINCIAL': '#8b5cf6',    // Púrpura
            'LOCAL': '#ec4899',         // Rosa
            'JUDICIAL': '#5b21b6'       // Violeta oscuro
        };
        return colores[tipo?.toUpperCase()] || '#ef4444';
    };

    const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

    const changeMonth = (increment) => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(prev.getMonth() + increment);
            return newDate;
        });
    };

    const handleDateClick = (day) => {
        const clickedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        setSelectedDate(clickedDate);
        setView('day');
    };

    const formatTime = (time) => {
        if (!time) return '';
        return time.toString().substring(0, 5);
    };

    const esVencimientoSRT = (event) =>
        !!(event.client && event.client.toUpperCase().includes('VENCIMIENTO SRT'));

    // Función para generar un color consistente basado en el CUIL del cliente
    const getColorForCliente = (cuil) => {
        if (!cuil) return 'hsl(207, 10%, 76%)';

        // Generar un hash del CUIL para obtener un color consistente
        let hash = 0;
        for (let i = 0; i < cuil.length; i++) {
            hash = cuil.charCodeAt(i) + ((hash << 5) - hash);
        }

        // Generar un color HSL con saturación y luminosidad fijas para mejor legibilidad
        const hue = Math.abs(hash) % 360;
        return `hsl(${hue}, 70%, 50%)`;
    };

    // Función para obtener el color de borde/fondo del evento basado en el cliente
    const getEventColor = (event) => {
        if (esVencimientoSRT(event)) return '#dc2626';
        if (event.cuilCliente) return getColorForCliente(event.cuilCliente);
        return 'hsl(207, 10%, 76%)';
    };

    const renderCalendar = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const daysInMonth = getDaysInMonth(year, month);
        const firstDay = getFirstDayOfMonth(year, month);
        const days = [];

        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="calendar-day-page empty"></div>);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dayAppointments = getAppointmentsForDate(date);
            const isToday = new Date().toDateString() === date.toDateString();
            const isSelected = selectedDate && selectedDate.toDateString() === date.toDateString();
            const isFeriado = esFeriado(date);
            const feriadoInfo = getFeriadoInfo(date);

            const feriadoColor = isFeriado ? getFeriadoColor(feriadoInfo?.tipo) : null;
            
            days.push(
                <div
                    key={day}
                    className={`calendar-day-page ${dayAppointments.length > 0 ? 'has-appointments' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${isFeriado ? 'feriado' : ''}`}
                    onClick={() => handleDateClick(day)}
                    title={isFeriado ? `${feriadoInfo.descripcion} (${feriadoInfo.tipo})` : ''}
                    style={isFeriado ? {
                        backgroundColor: `${feriadoColor}15`,
                        borderColor: feriadoColor,
                        borderWidth: '2px',
                        borderStyle: 'solid'
                    } : {}}
                >
                    {/* --- AQUÍ ESTÁ LA CORRECCIÓN: El número del día --- */}
                    <div className="day-number" style={isFeriado ? { color: feriadoColor, fontWeight: 'bold' } : {}}>
                        {day}
                        {isFeriado && (
                            <i className="bi bi-calendar-x-fill ms-1" style={{ fontSize: '0.8rem', color: feriadoColor }}></i>
                        )}
                    </div>

                    {isFeriado && (
                        <div className="feriado-indicator" style={{
                            backgroundColor: `${feriadoColor}25`,
                            color: feriadoColor,
                            fontWeight: '500'
                        }}>
                            {feriadoInfo.tipo?.toUpperCase() === 'JUDICIAL' && (
                                <span style={{ fontSize: '0.65rem', fontWeight: '700', display: 'block', marginBottom: '1px' }}>
                                    ⚖️ Inhábil Judicial
                                </span>
                            )}
                            <small>{feriadoInfo.descripcion}</small>
                        </div>
                    )}

                    {dayAppointments.length > 0 && (
                        <div className="appointment-list-mini">
                            {dayAppointments.map(app => {
                                const esVenc = esVencimientoSRT(app);
                                const eventColor = getEventColor(app);
                                const bgColor = esVenc
                                    ? 'rgba(220, 38, 38, 0.12)'
                                    : eventColor.replace(')', ', 0.15)').replace('hsl', 'hsla');

                                return (
                                    <div
                                        key={app.id}
                                        className="appointment-mini"
                                        style={{
                                            backgroundColor: bgColor,
                                            borderLeftColor: eventColor,
                                            fontWeight: esVenc ? '700' : undefined,
                                            borderLeftWidth: esVenc ? '4px' : undefined
                                        }}
                                        title={`${app.time ? app.time + ' - ' : ''}${app.client}${esVencimientoSRT(app) && app.nombreCompletoCliente ? ' - ' + app.nombreCompletoCliente : ''}`}
                                    >
                                        {esVenc && <span style={{ marginRight: '3px', fontSize: '0.75em' }}>⚠</span>}
                                        {!app.todoElDia && <span style={{opacity: 0.7, fontSize: '0.7em'}}>{formatTime(app.time)}</span>}
                                        <span
                                            className="appointment-title"
                                            style={esVenc ? { color: '#dc2626' } : undefined}
                                        >
                                            {app.client}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        }

        return days;
    };

    const renderDayView = () => {
        if (!selectedDate) return null;
        const dayAppointments = getAppointmentsForDate(selectedDate);
        const isFeriado = esFeriado(selectedDate);
        const feriadoInfo = getFeriadoInfo(selectedDate);

        return (
            <div className="card shadow-sm" style={{ border: 'none', borderRadius: '12px' }}>
                <div className="card-body" style={{ padding: '2rem' }}>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <button 
                            className="btn btn-outline-primary d-flex align-items-center gap-2" 
                            onClick={() => setView('month')}
                            style={{ borderRadius: '8px', padding: '0.6rem 1.2rem', fontWeight: '500' }}
                        >
                            <i className="bi bi-arrow-left"></i> Volver al mes
                        </button>
                        <h3 className="mb-0" style={{ fontWeight: '600', fontSize: '1.3rem', color: '#212529', textTransform: 'capitalize' }}>
                            <i className="bi bi-calendar-day me-2" style={{ color: '#3B82F6' }}></i>
                            {selectedDate.toLocaleDateString('es-ES', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })}
                            {isFeriado && (
                                <span className="badge ms-3" style={{ backgroundColor: getFeriadoColor(feriadoInfo?.tipo) }}>
                                    <i className={`bi ${feriadoInfo?.tipo?.toUpperCase() === 'JUDICIAL' ? 'bi-bank' : 'bi-calendar-x'} me-1`}></i>
                                    {feriadoInfo?.tipo?.toUpperCase() === 'JUDICIAL' ? 'Inhábil Judicial' : 'Feriado'}
                                </span>
                            )}
                        </h3>
                        <div style={{ width: '120px' }}></div>
                    </div>

                    {isFeriado && (
                        <div
                            className={`alert d-flex align-items-center mb-4 ${feriadoInfo?.tipo?.toUpperCase() === 'JUDICIAL' ? 'alert-danger' : 'alert-warning'}`}
                            role="alert"
                        >
                            <i className={`bi ${feriadoInfo?.tipo?.toUpperCase() === 'JUDICIAL' ? 'bi-bank' : 'bi-exclamation-triangle-fill'} me-2`} style={{ fontSize: '1.5rem' }}></i>
                            <div>
                                <strong>
                                    {feriadoInfo?.tipo?.toUpperCase() === 'JUDICIAL' ? 'Inhábil Judicial (Córdoba):' : 'Día Feriado:'}
                                </strong>{' '}{feriadoInfo.descripcion}
                                <br />
                                <small className="text-muted">Este día no se considera hábil para el cálculo de fechas de trámites</small>
                            </div>
                        </div>
                    )}

                    <div className="day-events-list">
                        {dayAppointments.length > 0 ? (
                            dayAppointments.map(event => {
                                const esVenc = esVencimientoSRT(event);
                                const eventColor = getEventColor(event);
                                return (
                                    <div
                                        key={event.id}
                                        className="card mb-3 shadow-sm"
                                        style={{
                                            border: esVenc ? `2px solid ${eventColor}` : 'none',
                                            borderLeft: `${esVenc ? '6px' : '5px'} solid ${eventColor}`,
                                            borderRadius: '8px',
                                            background: esVenc ? 'rgba(220, 38, 38, 0.06)' : `${eventColor}08`,
                                            transition: 'all 0.2s ease',
                                            boxShadow: esVenc ? '0 0 0 0 rgba(220,38,38,0.4)' : undefined
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateX(5px)';
                                            e.currentTarget.style.boxShadow = esVenc
                                                ? '0 4px 16px rgba(220,38,38,0.35)'
                                                : '0 4px 12px rgba(0,0,0,0.15)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateX(0)';
                                            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                                        }}
                                    >
                                        <div className="card-body" style={{ padding: '1.5rem' }}>
                                            {esVenc && (
                                                <div
                                                    className="d-flex align-items-center gap-2 mb-3 px-3 py-2 rounded"
                                                    style={{ backgroundColor: '#dc2626', color: 'white', fontWeight: '700' }}
                                                >
                                                    <span style={{ fontSize: '1.1rem' }}>⚠</span>
                                                    PLAZO CRÍTICO — Apelación/telegrama requerido antes del vencimiento
                                                </div>
                                            )}
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div className="flex-grow-1">
                                                    <div className="d-flex align-items-center gap-3 mb-3">
                                                        <div 
                                                            className="badge" 
                                                            style={{ 
                                                                backgroundColor: eventColor, 
                                                                color: 'white',
                                                                padding: '0.6rem 1rem',
                                                                borderRadius: '6px',
                                                                fontWeight: '600',
                                                                fontSize: '0.9rem'
                                                            }}
                                                        >
                                                            {event.todoElDia ? (
                                                                <><i className="bi bi-clock-fill me-2"></i>Todo el día</>
                                                            ) : (
                                                                <><i className="bi bi-clock me-2"></i>{formatTime(event.horaInicio)}</>
                                                            )}
                                                        </div>
                                                        <h4 className="mb-0" style={{ fontWeight: '600', fontSize: '1.2rem', color: '#111827' }}>
                                                            {event.client}
                                                        </h4>
                                                    </div>
                                                    
                                                    {event.nombreCompletoCliente && (
                                                        <div 
                                                            className="mb-2 p-2" 
                                                            style={{ 
                                                                background: 'rgba(59, 130, 246, 0.08)', 
                                                                borderRadius: '6px',
                                                                display: 'inline-block'
                                                            }}
                                                        >
                                                            <small style={{ color: '#1e40af', fontWeight: '600' }}>
                                                                <i className="bi bi-person-circle me-2"></i>
                                                                Cliente: {event.nombreCompletoCliente}
                                                                {event.cuilCliente && ` (CUIL: ${event.cuilCliente})`}
                                                            </small>
                                                        </div>
                                                    )}
                                                    
                                                    <p className="mb-2" style={{ color: '#6B7280', fontSize: '0.95rem', fontWeight: '500' }}>
                                                        <i className="bi bi-tag me-2"></i>{event.type}
                                                    </p>
                                                    
                                                    {event.description && (
                                                        <p className="mb-0" style={{ color: '#6B7280', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                            <i className="bi bi-chat-left-text me-2"></i>{event.description}
                                                        </p>
                                                    )}
                                                </div>
                                                
                                                <div className="d-flex flex-column gap-2">
                                                    <button
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => {
                                                            setEventoToEdit(event);
                                                            setIsModificarModalOpen(true);
                                                        }}
                                                        title="Editar evento"
                                                        style={{ borderRadius: '6px', padding: '0.4rem 0.6rem' }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => handleEliminarEvento(event.id)}
                                                        title="Eliminar evento"
                                                        style={{ borderRadius: '6px', padding: '0.4rem 0.6rem' }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-center py-5">
                                <i className="bi bi-calendar-x" style={{ fontSize: '4rem', color: '#cbd5e1' }}></i>
                                <p className="mt-3 mb-3" style={{ color: '#64748b', fontSize: '1.1rem' }}>No hay eventos programados para este día</p>
                                <button
                                    className="btn btn-success"
                                    onClick={() => setIsModalOpen(true)}
                                    style={{ borderRadius: '8px', padding: '0.6rem 1.5rem', fontWeight: '500' }}
                                >
                                    <i className="bi bi-plus-circle me-2"></i>Crear evento
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="calendario-page-container">
            {/* Header Moderno */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="text-primary mb-1" style={{ fontWeight: '600', fontSize: '1.75rem' }}>
                        <i className="bi bi-calendar-event me-2"></i>Calendario de Eventos
                    </h2>
                    <p className="text-muted mb-0" style={{ fontSize: '0.95rem' }}>Gestión y seguimiento de eventos y citas</p>
                </div>
                <button
                    className="btn btn-success d-flex align-items-center gap-2"
                    onClick={() => setIsModalOpen(true)}
                    style={{ borderRadius: '8px', padding: '0.6rem 1.5rem', fontWeight: '500' }}
                >
                    <i className="bi bi-plus-circle me-1"></i>Crear Evento
                </button>
            </div>

            {view === 'month' ? (
                <div className="card shadow-sm" style={{ border: 'none', borderRadius: '12px' }}>
                    <div className="card-body" style={{ padding: '1.5rem' }}>
                        {/* Controles del mes modernizados */}
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <button 
                                className="btn btn-outline-primary" 
                                onClick={() => changeMonth(-1)}
                                style={{ borderRadius: '50%', width: '45px', height: '45px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '1.5rem' }}
                            >
                                ‹
                            </button>
                            <h3 className="mb-0" style={{ fontWeight: '600', fontSize: '1.4rem', color: '#212529', textTransform: 'capitalize' }}>
                                <i className="bi bi-calendar3 me-2"></i>
                                {currentDate.toLocaleString('es-ES', { month: 'long', year: 'numeric' })}
                            </h3>
                            <button 
                                className="btn btn-outline-primary" 
                                onClick={() => changeMonth(1)}
                                style={{ borderRadius: '50%', width: '45px', height: '45px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '1.5rem' }}
                            >
                                ›
                            </button>
                        </div>

                        {/* Leyenda de colores de feriados */}
                        {feriados.length > 0 && (
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '12px',
                                padding: '12px 16px',
                                marginBottom: '16px',
                                backgroundColor: '#f8fafc',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                alignItems: 'center'
                            }}>
                                <small style={{ fontWeight: '600', color: '#64748b', marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                                    <i className="bi bi-info-circle me-2"></i>Tipos de Feriados:
                                </small>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#ef4444' }}></div>
                                    <small style={{ color: '#64748b' }}>Nacional</small>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#3b82f6' }}></div>
                                    <small style={{ color: '#64748b' }}>Trasladable</small>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#10b981' }}></div>
                                    <small style={{ color: '#64748b' }}>Puente</small>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#f59e0b' }}></div>
                                    <small style={{ color: '#64748b' }}>No Laborable</small>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#5b21b6' }}></div>
                                    <small style={{ color: '#64748b' }}>⚖️ Inhábil Judicial</small>
                                </div>
                            </div>
                        )}

                        <div className="calendar-grid-wrapper">
                            <div className="calendar-grid-page">
                                <div className="weekday">Dom</div>
                                <div className="weekday">Lun</div>
                                <div className="weekday">Mar</div>
                                <div className="weekday">Mié</div>
                                <div className="weekday">Jue</div>
                                <div className="weekday">Vie</div>
                                <div className="weekday">Sáb</div>
                                {renderCalendar()}
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                renderDayView()
            )}

            <CrearEventoModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSaveEvento}
                selectedDate={selectedDate || new Date()}
            />

            <ModificarEventoModal
                isOpen={isModificarModalOpen}
                onClose={() => {
                    setIsModificarModalOpen(false);
                    setEventoToEdit(null);
                }}
                onSave={handleModificarEvento}
                evento={eventoToEdit}
            />

            <CustomModal
                show={modalState.show}
                onHide={hideModal}
                title={modalState.title}
                message={modalState.message}
                type={modalState.type}
                onConfirm={modalState.onConfirm}
                confirmText={modalState.confirmText}
                cancelText={modalState.cancelText}
                showCancel={modalState.showCancel}
                confirmVariant={modalState.confirmVariant}
            />
        </div>
    );
};

export default CalendarioPage;

